import { useCallback, useEffect, useMemo } from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  FormAutocomplete,
  AutocompleteOption,
} from '@/components/ui/custom/Autocomplete/Autocomplete';
import useDialogs from '@/hooks/useDialogs';
import { updateAction } from '@/redux/nodes/actions';
import { selectBrainId, selectDialogId } from '@/redux/session/selectors';
import { EventAction } from '@/shared/types/action';
import { createYupResolver } from '@/util/forms/createYupResolver';
import { eventActionSchema2 } from '@/util/validation/schemas/action-schemas';

const JumpTo = ({ action }: { action: EventAction }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const brainId = useSelector(selectBrainId);
  const dialogId = useSelector(selectDialogId);
  const { detailedOptions } = useDialogs(brainId, dialogId);

  // Transform detailedOptions to AutocompleteOption format
  const autocompleteOptions = useMemo<AutocompleteOption[]>(() => {
    return detailedOptions.map((option) => ({
      value: option.value,
      label: option.label,
      category: option.dialog_name,
    }));
  }, [detailedOptions]);

  const defaultTriggerNodeId = useMemo(() => {
    return action.trigger_node_id || '';
  }, [action.trigger_node_id]);

  type JumpToForm = { jump_to?: string };
  const {
    control,
    formState: { errors },
    setValue,
  } = useForm<JumpToForm>({
    defaultValues: { jump_to: defaultTriggerNodeId },
    resolver: createYupResolver<JumpToForm>(eventActionSchema2),
  });

  const handleChange = useCallback(
    (value: string) => {
      dispatch(updateAction({ actionId: action.action_id, action: { trigger_node_id: value } }));
    },
    [action.action_id, dispatch]
  );

  // Sync Redux state to form only when changed externally (not from handleChange)
  useEffect(() => {
    if (defaultTriggerNodeId) {
      setValue('jump_to', defaultTriggerNodeId, { shouldValidate: true });
    }
  }, [defaultTriggerNodeId, setValue]);

  return (
    <FormAutocomplete
      control={control}
      options={autocompleteOptions}
      name="jump_to"
      placeholder={t('dialog.jump_to.placeholder')}
      error={!!errors.jump_to}
      errorMessage={errors.jump_to?.message}
      onChange={handleChange}
    />
  );
};

export default JumpTo;
